import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useApolloClient,
  useMutation as useApolloMutation,
} from "@apollo/client/react";
import { toast } from "sonner";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import {
  GetDealsDocument,
  UpdateDealDocument,
  type GetDealsQuery,
  type UpdateDealMutation,
  type Deals,
} from "@/src/gql/graphql";
import type { Deal, UpdateDealInput } from "@/lib/types/database/deals";
import {
  evaluateQualityRules,
  type QualityIssue,
} from "@/lib/deal-quality/quality-rules";
import { adaptGraphQLDealsArrayToDealArray } from "@/lib/types/database/graphql-adapters";
import { toQualityDeals } from "@/lib/deal-quality/to-quality-deal";
import { fetchAllGraphqlNodes } from "@/lib/hooks/api/dashboard/paginate";
import {
  stringifyDecimalFields,
  numberifyDecimalFields,
} from "./use-deals-hooks/decimal-fields";

// Node type derived from the GetDeals query shape (single source of truth).
type GetDealsNode = NonNullable<
  GetDealsQuery["dealsCollection"]
>["edges"][number]["node"];

/**
 * Deal Quality Hooks
 * Provides operations for fetching quality issues and applying fixes
 */

export const useDealQualityHooks = () => {
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();

  /**
   * Fetch ALL deals (paged to completion) and evaluate quality rules.
   *
   * Pages through GetDeals via fetchAllGraphqlNodes so the quality analysis
   * runs over the COMPLETE set, never a single capped page (the old
   * `first: 1000` silently truncated the issue counts past 1000 deals).
   */
  const useQualityIssues = () => {
    const { data, isLoading, error, refetch } = useQuery<QualityIssue[]>({
      queryKey: ["deal-quality-issues"],
      queryFn: async () => {
        const nodes = await fetchAllGraphqlNodes<GetDealsQuery, GetDealsNode>(
          apolloClient,
          GetDealsDocument,
          {}, // preserve existing behavior: no GraphQL filter (set bounded by evaluateQualityRules)
          (d) => d.dealsCollection,
        );
        const graphqlDeals: Deals[] = nodes.filter(
          (node): node is Deals => node != null,
        );
        const deals = adaptGraphQLDealsArrayToDealArray(graphqlDeals);
        return evaluateQualityRules(toQualityDeals(deals));
      },
    });

    return {
      data: data ?? [],
      isLoading,
      error: error instanceof Error ? error : error ? new Error(String(error)) : null,
      refetch,
    };
  };

  /**
   * Get deals for a specific quality issue (over the COMPLETE deal set).
   *
   * Returns the FULL `deals` rows alongside the issue. `QualityIssue["deals"]`
   * is the scoring package's own projection — it carries no `company_id` and no
   * `created_at`, so it cannot drive the deal row actions, which need a real
   * row. This hook has the rows in hand anyway; handing them back is cheaper
   * and more honest than casting the projection into a shape it does not have
   * (#2633).
   */
  const useIssueDeals = (issueId: string) => {
    const { data, isLoading, error, refetch } = useQuery<{
      issue: QualityIssue | undefined;
      dealsById: Record<string, Deal>;
    }>({
      queryKey: ["deal-quality-issue", issueId],
      queryFn: async () => {
        const nodes = await fetchAllGraphqlNodes<GetDealsQuery, GetDealsNode>(
          apolloClient,
          GetDealsDocument,
          {}, // preserve existing behavior: no GraphQL filter (set bounded by evaluateQualityRules)
          (d) => d.dealsCollection,
        );
        const graphqlDeals: Deals[] = nodes.filter(
          (node): node is Deals => node != null,
        );
        const deals = adaptGraphQLDealsArrayToDealArray(graphqlDeals);
        const issues = evaluateQualityRules(toQualityDeals(deals));
        const dealsById: Record<string, Deal> = {};
        for (const deal of deals) dealsById[deal.id] = deal;
        return { issue: issues.find((i) => i.id === issueId), dealsById };
      },
    });

    return {
      data: data?.issue,
      dealsById: data?.dealsById ?? {},
      isLoading,
      error: error instanceof Error ? error : error ? new Error(String(error)) : null,
      refetch,
    };
  };

  /**
   * Update a single deal
   */
  const useFixDeal = () => {
    const [updateDeal] = useApolloMutation<UpdateDealMutation>(
      UpdateDealDocument
    );

    return {
      mutateAsync: async (input: Partial<Deal> & { id: string }) => {
        const { id, ...updateInput } = input;

        // Filter out Apollo cache metadata fields
        const cleanInput = Object.fromEntries(
          Object.entries(updateInput).filter(([key]) => !key.startsWith("__"))
        ) as UpdateDealInput;

        const finalInput = stringifyDecimalFields(cleanInput);

        const { data } = await updateDeal({
          variables: { id, input: finalInput },
        });

        if (!data?.updatedealsCollection?.records?.[0]) {
          throw new Error("Failed to update deal");
        }

        void queryClient.invalidateQueries({ queryKey: ["deals"] });
        return numberifyDecimalFields(data.updatedealsCollection.records[0] as Deal);
      },
      isPending: false,
    };
  };

  /**
   * Update multiple deals in bulk
   */
  const useFixMultipleDeals = () => {
    const [updateDeal] = useApolloMutation<UpdateDealMutation>(
      UpdateDealDocument
    );

    return {
      mutateAsync: async (
        deals: Array<Partial<Deal> & { id: string }>,
        onProgress?: (current: number, total: number) => void
      ) => {
        const results: Deal[] = [];
        const errors: Array<{ dealId: string; error: string }> = [];

        for (let i = 0; i < deals.length; i++) {
          const deal = deals[i];
          try {
            const { id, ...updateInput } = deal;

            // Filter out Apollo cache metadata fields
            const cleanInput = Object.fromEntries(
              Object.entries(updateInput).filter(
                ([key]) => !key.startsWith("__")
              )
            ) as UpdateDealInput;

            const finalInput = stringifyDecimalFields(cleanInput);

            const { data } = await updateDeal({
              variables: { id, input: finalInput },
            });

            if (data?.updatedealsCollection?.records?.[0]) {
              results.push(numberifyDecimalFields(data.updatedealsCollection.records[0] as Deal));
            } else {
              errors.push({
                dealId: id,
                error: "Failed to update deal",
              });
            }
          } catch (error) {
            errors.push({
              dealId: deal.id,
              error: error instanceof Error ? error.message : "Unknown error",
            });
          }

          onProgress?.(i + 1, deals.length);
        }

        void queryClient.invalidateQueries({ queryKey: ["deals"] });

        if (errors.length > 0) {
          toast.error(
            `Updated ${results.length} deals, ${errors.length} failed`
          );
        } else {
          toast.success(`Successfully updated ${results.length} deals`);
        }

        return {
          results,
          errors,
          successCount: results.length,
          errorCount: errors.length,
        };
      },
      isPending: false,
    };
  };

  return {
    useQualityIssues,
    useIssueDeals,
    useFixDeal,
    useFixMultipleDeals,
  };
};
